<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NucleoWorkOrderServiceDocumentGroup from './NucleoWorkOrderServiceDocumentGroup.vue';
import { useNucleoWorkOrderServiceDocuments } from '../composables/useNucleoWorkOrderServiceDocuments';

const props = defineProps({
  parties: { type: Array, default: () => [] },
  transactionId: { type: [String, Number], default: null },
});
const emit = defineEmits(['uploaded']);
const { t } = useI18n();
const groups = ['vendedores', 'compradores', 'property'];
const sections = ref({});
const documents = useNucleoWorkOrderServiceDocuments({
  transactionId: computed(() => props.transactionId),
  t,
  notify: useAlert,
});

const section = key => {
  if (!sections.value[key]) {
    sections.value[key] = {
      enabled: false,
      open: false,
      partyId: '',
      files: [],
      typeIds: [],
      documentTypes: [],
    };
  }
  return sections.value[key];
};
const isProperty = key => key === 'property';
const titleFor = key => {
  if (key === 'vendedores') {
    return t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.GROUPS.VENDEDORES');
  }
  if (key === 'compradores') {
    return t(
      'NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.GROUPS.COMPRADORES'
    );
  }
  return t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.GROUPS.PROPERTY');
};
const peopleFor = key => {
  const role = key === 'vendedores' ? 'vendedor' : 'comprador';
  return props.parties.filter(party => party.role === role);
};
const optionsFor = key =>
  isProperty(key) ? documents.propertyTypes.value : documents.partyTypes.value;
const documentTypesFor = (key, ids) =>
  optionsFor(key)
    .filter(option => ids.includes(String(option.id)))
    .map(option => ({
      id: option.id,
      name: option.name || option.nome,
      validity_days: option.validity_days || option.validade_dias || '',
      issued_on: '',
      expires_on: '',
    }));
const update = (key, payload) => {
  Object.assign(section(key), payload);
  if (payload.typeIds) {
    section(key).documentTypes = documentTypesFor(key, payload.typeIds);
  }
};
const upload = async key => {
  const current = section(key);
  const artifacts = isProperty(key)
    ? await documents.uploadProperty(current)
    : await documents.uploadParty(current);
  if (artifacts.length) {
    emit('uploaded', artifacts);
    update(key, { files: [] });
  }
};

watch(
  () => props.transactionId,
  () => documents.load(),
  { immediate: true }
);
</script>

<template>
  <section class="grid gap-3">
    <div>
      <h3 class="text-sm font-semibold text-lais-text">
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.TITLE') }}
      </h3>
      <p class="mt-1 text-sm text-lais-text-body">
        {{ t('NUCLEO_WORK_ORDERS.DETAIL.EXECUTION.DOCUMENTS.DESCRIPTION') }}
      </p>
    </div>
    <NucleoWorkOrderServiceDocumentGroup
      v-for="key in groups"
      :key="key"
      :is-property="isProperty(key)"
      :label="titleFor(key)"
      :loading="documents.loading.value"
      :document-types="section(key).documentTypes"
      :options="optionsFor(key)"
      :people="peopleFor(key)"
      :section="section(key)"
      :uploading="documents.uploading.value"
      @update="update(key, $event)"
      @upload="upload(key)"
    />
  </section>
</template>
